import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Popover } from '@mui/material';
import { KeyboardEvent, useRef, useState } from 'react';
import { TextBlock } from '../../Atoms/Text';
import { SCHEDULER_SELECT_CLASS } from './schedulerFieldClasses';
import { DAYS_SOME_MONTHS_LACK, dayOfMonthLabel, LAST, ordinal } from './scheduleRule';

const DAYS = Array.from({ length: 31 }, (_, index) => index + 1);
const OPTIONS = [...DAYS, LAST];
const LAST_INDEX = OPTIONS.length - 1;
const WEEK = 7;

// Arrow keys move along the seven-day rows, with Last day of month a row of its own; the result is clamped.
const KEY_STEPS: Record<string, (index: number) => number> = {
  ArrowRight: (index) => index + 1,
  ArrowLeft: (index) => index - 1,
  ArrowDown: (index) => index + WEEK,
  ArrowUp: (index) => (index === LAST_INDEX ? LAST_INDEX - 1 : index - WEEK),
  Home: () => 0,
  End: () => LAST_INDEX
};

const dayClass = (selected: boolean, fallsBack: boolean): string => {
  if (selected) return 'bg-indigo text-white';
  return `hover:bg-gray-100 ${fallsBack ? 'text-amber-700' : 'text-gray-800'}`;
};

export interface DayOfMonthPickerProps {
  value: number;
  disabled: boolean;
  // The note that explains the chosen day, if any.
  describedBy?: string;
  onChange: (day: number) => void;
}

/** A field that opens a grid of days 1–31 and Last day of month, for a monthly schedule's day. */
export const DayOfMonthPicker = ({ value, disabled, describedBy, onChange }: DayOfMonthPickerProps) => {
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);
  // A saved day outside the list still leaves the group one stop to tab into.
  const tabStop = Math.max(OPTIONS.indexOf(value), 0);

  const pick = (day: number) => {
    onChange(day);
    setAnchor(null);
  };

  const moveWithKeys = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const step = KEY_STEPS[event.key];
    if (!step) return;
    event.preventDefault();
    const next = Math.min(Math.max(step(index), 0), LAST_INDEX);
    onChange(OPTIONS[next]);
    optionRefs.current[next]?.focus();
  };

  const optionProps = (option: number, index: number) => ({
    type: 'button' as const,
    role: 'radio',
    'aria-checked': option === value,
    tabIndex: index === tabStop ? 0 : -1,
    ref: (element: HTMLButtonElement | null) => {
      optionRefs.current[index] = element;
    },
    onClick: () => pick(option),
    onKeyDown: (event: KeyboardEvent<HTMLButtonElement>) => moveWithKeys(event, index)
  });

  return (
    <>
      <button
        type="button"
        className={`${SCHEDULER_SELECT_CLASS} flex items-center justify-between gap-2`}
        style={{ minWidth: 160 }}
        aria-label={`Day of month: ${dayOfMonthLabel(value)}`}
        aria-describedby={describedBy}
        aria-haspopup="dialog"
        aria-expanded={!!anchor}
        disabled={disabled}
        onClick={(e) => setAnchor(e.currentTarget)}>
        {dayOfMonthLabel(value)}
        <ArrowDropDownIcon fontSize="small" />
      </button>
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        // Opens on the chosen day, rather than on the dialog that holds 32 options.
        TransitionProps={{ onEntered: () => optionRefs.current[tabStop]?.focus() }}
        slotProps={{ paper: { role: 'dialog', 'aria-modal': true, 'aria-label': 'Choose a day of the month' } }}>
        <div
          className="p-3 text-sm"
          role="radiogroup"
          aria-label="Day of month">
          <div className="grid grid-cols-7 gap-1">
            {DAYS.map((day, index) => (
              <button
                key={day}
                {...optionProps(day, index)}
                className={`w-8 h-8 rounded ${dayClass(day === value, DAYS_SOME_MONTHS_LACK.includes(day))}`}
                aria-label={ordinal(day)}>
                {day}
              </button>
            ))}
          </div>
          <button
            {...optionProps(LAST, LAST_INDEX)}
            className="flex items-center gap-2 mt-2 text-gray-800">
            <span
              aria-hidden="true"
              className={`w-4 h-4 rounded-full border-2 ${value === LAST ? 'border-indigo bg-indigo' : 'border-gray-400'}`}
            />
            {dayOfMonthLabel(LAST)}
          </button>
          <TextBlock
            size="xs"
            color="grey6"
            className="mt-1">
            29–31 fall back to the last day in shorter months.
          </TextBlock>
        </div>
      </Popover>
    </>
  );
};
